import type { TrackingFieldDto } from 'src/app/types/dto/tracking-field.dto'
import type {
  ExtraCategoryDefinition,
  ExtraScenario,
  HouseTypeDefinition,
  JobScenario,
  LocationDefinition,
  TaskDefinition,
  VariationScenario,
} from './demo-api.fixtures.types'


// Editing guide: README.md#updating-mock-data
// The fixture generator derives API records from the inputs in this file.


// ! Keys are referenced by scenarios. Manager and sales-rep order also determines generated user IDs.
// ! careful when changing order or removing existing data

export const divisions = {
  newHomes: 'New Homes',
  developments: 'Developments',
  customHomes: 'Custom Homes',
} as const satisfies Record<string, string>

export const managers = {
  avaPatel: 'Ava Patel',
  jordanLee: 'Jordan Lee',
  samWilliams: 'Sam Williams',
  alexMorgan: 'Alex Morgan',
} as const satisfies Record<string, string>

export const salesReps = {
  miaChen: 'Mia Chen',
  noahTaylor: 'Noah Taylor',
} as const satisfies Record<string, string>

export const locations = {
  alkimos: { suburb: 'Alkimos', council: 'City of Wanneroo', region: 'Metro North', postcode: '6038', estate: 'Coastal Rise' },
  baldivis: { suburb: 'Baldivis', council: 'City of Rockingham', region: 'Metro South', postcode: '6171', estate: 'Tuart Rise' },
  bunbury: { suburb: 'Bunbury', council: 'City of Bunbury', region: 'South West', postcode: '6230', estate: '' },
} as const satisfies Record<string, LocationDefinition>

export const houseTypes = {
  singleStorey: { description: 'Single Storey', baseArea: 185, frontage: 12.5, landTitleType: 'Green Title' },
  doubleStorey: { description: 'Double Storey', baseArea: 285, frontage: 15, landTitleType: 'Green Title' },
  townhouse: { description: 'Townhouse', baseArea: 155, frontage: 10, landTitleType: 'Survey Strata' },
} as const satisfies Record<string, HouseTypeDefinition>

type ConfiguredJobScenario = Omit<JobScenario, 'location' | 'clientLocation' | 'division' | 'manager' | 'salesRep' | 'houseType'> & {
  location: keyof typeof locations
  clientLocation: keyof typeof locations
  division: keyof typeof divisions
  manager: keyof typeof managers | null
  salesRep: keyof typeof salesReps
  houseType: keyof typeof houseTypes
}

// startDay is relative to the date supplied to createDemoFixtures.
// ! Keep existing keys and order stable: other sections use the keys, while order generates job numbers and IDs.
export const scenarios = [
  { key: 'anderson', name: 'Anderson', startDay: -570, location: 'alkimos', clientLocation: 'baldivis', division: 'newHomes', manager: 'alexMorgan', salesRep: 'miaChen', houseType: 'singleStorey', price: 385000, margin: 18, state: 'normal' },
  { key: 'bennett', name: 'Bennett', startDay: -240, location: 'baldivis', clientLocation: 'bunbury', division: 'developments', manager: 'avaPatel', salesRep: 'noahTaylor', houseType: 'townhouse', price: 720000, margin: 22, state: 'normal' },
  { key: 'campbell', name: 'Campbell', startDay: -165, location: 'bunbury', clientLocation: 'alkimos', division: 'newHomes', manager: 'jordanLee', salesRep: 'miaChen', houseType: 'townhouse', price: 495000, margin: 16, state: 'normal' },
  { key: 'diaz', name: 'Diaz', startDay: -100, location: 'alkimos', clientLocation: 'baldivis', division: 'customHomes', manager: 'samWilliams', salesRep: 'noahTaylor', houseType: 'doubleStorey', price: 825000, margin: 20, state: 'normal', landZoning: 'Rural Residential' },
  { key: 'evans', name: 'Evans', startDay: -20, location: 'baldivis', clientLocation: 'bunbury', division: 'newHomes', manager: 'avaPatel', salesRep: 'noahTaylor', houseType: 'singleStorey', price: 410000, margin: 19, state: 'normal' },
  { key: 'foster', name: 'Foster', startDay: 30, location: 'bunbury', clientLocation: 'alkimos', division: 'developments', manager: 'samWilliams', salesRep: 'miaChen', houseType: 'townhouse', price: 465000, margin: 21, state: 'normal' },
  { key: 'gupta', name: 'Gupta', startDay: 100, location: 'alkimos', clientLocation: 'baldivis', division: 'customHomes', manager: 'jordanLee', salesRep: 'noahTaylor', houseType: 'singleStorey', price: 445000, margin: 17, state: 'waiting' },
  { key: 'harris', name: 'Harris', startDay: 180, location: 'baldivis', clientLocation: 'bunbury', division: 'developments', manager: null, salesRep: 'miaChen', houseType: 'doubleStorey', price: 680000, margin: 23, state: 'sales' },
  { key: 'ito', name: 'Ito', startDay: -75, location: 'bunbury', clientLocation: 'alkimos', division: 'customHomes', manager: 'avaPatel', salesRep: 'miaChen', houseType: 'townhouse', price: 510000, margin: 15, state: 'hold' },
  { key: 'jones', name: 'Jones', startDay: -70, location: 'alkimos', clientLocation: 'baldivis', division: 'developments', manager: null, salesRep: 'noahTaylor', houseType: 'singleStorey', price: 395000, margin: 18, state: 'cancelled' },
  { key: 'kaur', name: 'Kaur', startDay: -200, location: 'baldivis', clientLocation: 'bunbury', division: 'newHomes', manager: 'jordanLee', salesRep: 'noahTaylor', houseType: 'doubleStorey', price: 760000, margin: -3, state: 'problem' },
  { key: 'lewis', name: 'Lewis', startDay: -210, location: 'bunbury', clientLocation: 'alkimos', division: 'customHomes', manager: 'samWilliams', salesRep: 'miaChen', houseType: 'singleStorey', price: 430000, margin: 24, state: 'normal' },
] as const satisfies readonly ConfiguredJobScenario[]

type JobKey = typeof scenarios[number]['key']

// ! Order maps lifecycle states to activity names and generates activity IDs/codes.
export const activityNames = ['10 Sales', '20 Pre-Construction', '30 Site Start', '40 Construction', '50 Handover', '60 Cancelled']

// Offsets are calendar days from job start; share is the fraction of contract value claimed at the milestone.
// ! IDs may be stored in dashboards; order contributes to generated job-task IDs.
export const taskDefinitions = [
  { id: 7304, title: 'Building Approval', code: 'APPROVAL', type: 'Pre-Construction', offset: -45, duration: 20, vendor: '', share: 0 },
  { id: 7305, title: 'Site Start', code: 'START', type: 'Construction', offset: 0, duration: 3, vendor: 'Coastal Earthworks', share: 0 },
  { id: 7301, title: 'Slab Complete', code: 'SLAB', type: 'Construction', offset: 14, duration: 10, vendor: 'West Coast Concrete', share: 0.15 },
  { id: 7302, title: 'Frame Complete', code: 'FRAME', type: 'Construction', offset: 45, duration: 20, vendor: 'Jarrah Carpentry', share: 0.2 },
  { id: 7309, title: 'Steel Frame Complete', code: 'FRAME', type: 'Construction', offset: 45, duration: 20, vendor: 'Coastal Steel Frames', share: 0.2 },
  { id: 7306, title: 'Lock-Up', code: 'LOCKUP', type: 'Construction', offset: 100, duration: 25, vendor: 'Coastal Glazing', share: 0.25 },
  { id: 7307, title: 'Fixing Complete', code: 'FIXING', type: 'Construction', offset: 135, duration: 30, vendor: 'Jarrah Carpentry', share: 0.2 },
  { id: 7308, title: 'Landscaping', code: 'LANDSCAPE', type: 'Handover', offset: 170, duration: 5, vendor: 'Banksia Landscapes', share: 0 },
  { id: 7303, title: 'Practical Completion', code: 'PC', type: 'Handover', offset: 180, duration: 7, vendor: '', share: 0.15 },
] as const satisfies readonly TaskDefinition[]

export const variationScenarios = [
  { job: 'campbell', days: -12, amount: 8800, status: 'posted' },
  { job: 'diaz', days: -13, amount: -2200, status: 'posted' },
  { job: 'kaur', days: -20, amount: 8800, status: 'created' },
] as const satisfies readonly (Omit<VariationScenario, 'job'> & { job: JobKey })[]

export const extraCategories = {
  electrical: { costCentreDescription: 'Electrical', costCentreId: 100, phaseId: 10, purchaseOrderVendorId: 200, vendorCode: 'V-1', vendorName: 'Bright Spark Electrical', positiveDescription: 'Lighting upgrade' },
  kitchen: { costCentreDescription: 'Kitchen', costCentreId: 101, phaseId: 11, purchaseOrderVendorId: 201, vendorCode: 'V-2', vendorName: 'Coastal Cabinets', positiveDescription: 'Joinery adjustment' },
  siteWorks: { costCentreDescription: 'Site Works', costCentreId: 102, phaseId: 12, purchaseOrderVendorId: 202, vendorCode: 'V-3', vendorName: 'Coastal Earthworks', positiveDescription: 'Additional excavation' },
} as const satisfies Record<string, ExtraCategoryDefinition>

// ! Order generates extra, invoice and purchase-order IDs.
export const extraScenarios = [
  { job: 'campbell', days: -28, category: 'electrical', reason: 'Client selection', amount: 3200 },
  { job: 'campbell', days: -7, category: 'kitchen', reason: 'Client selection', amount: 1800 },
  { job: 'diaz', days: -14, category: 'electrical', reason: 'Scope reduction', amount: -2000 },
  { job: 'evans', days: -3, category: 'siteWorks', reason: 'Unforeseen ground conditions', amount: 4600 },
  { job: 'ito', days: -45, category: 'siteWorks', reason: 'Unforeseen ground conditions', amount: 7500 },
  { job: 'kaur', days: -1, category: 'kitchen', reason: 'Estimating omission', amount: 12000 },
  { job: 'lewis', days: -21, category: 'electrical', reason: '', amount: 0 },
  { job: 'anderson', days: -400, category: 'siteWorks', reason: 'Unforeseen ground conditions', amount: 2500 },
] as const satisfies readonly (Omit<ExtraScenario, 'job' | 'category'> & {
  job: JobKey
  category: keyof typeof extraCategories
})[]

// ! dataField values may be stored in dashboard configuration; renaming one also requires updating every job below.
export const trackingFields = [
  { dataField: 'customerSatisfaction', displayName: 'Customer Satisfaction', datatype: 'number' },
  { dataField: 'qualityScore', displayName: 'Quality Score', datatype: 'number' },
  { dataField: 'defectsOutstanding', displayName: 'Defects Outstanding', datatype: 'number' },
] as const satisfies readonly TrackingFieldDto[]

type TrackingFieldKey = typeof trackingFields[number]['dataField']

// Empty strings represent values that have not been recorded.
export const trackingValues = {
  anderson: { customerSatisfaction: '92', qualityScore: '95.5', defectsOutstanding: '0' },
  bennett: { customerSatisfaction: '88', qualityScore: '90', defectsOutstanding: '0' },
  campbell: { customerSatisfaction: '', qualityScore: '87.5', defectsOutstanding: '3' },
  diaz: { customerSatisfaction: '', qualityScore: '92', defectsOutstanding: '1' },
  evans: { customerSatisfaction: '', qualityScore: '', defectsOutstanding: '' },
  foster: { customerSatisfaction: '', qualityScore: '', defectsOutstanding: '' },
  gupta: { customerSatisfaction: '', qualityScore: '', defectsOutstanding: '' },
  harris: { customerSatisfaction: '', qualityScore: '', defectsOutstanding: '' },
  ito: { customerSatisfaction: '', qualityScore: '78', defectsOutstanding: '2' },
  jones: { customerSatisfaction: '', qualityScore: '', defectsOutstanding: '' },
  kaur: { customerSatisfaction: '', qualityScore: '65.5', defectsOutstanding: '7' },
  lewis: { customerSatisfaction: '96', qualityScore: '98', defectsOutstanding: '0' },
} satisfies Record<JobKey, Record<TrackingFieldKey, string>>